import api from './api';

// Interfaces
export interface Form {
  id: number;
  name: string;
  description?: string;
  version: number;
  active: boolean;
  is_template: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  creator_name?: string;
  creator_email?: string;
  total_categories?: number;
  total_questions?: number;
  total_audits?: number;
  total_weighting?: number;
}

export interface FormCategory {
  id: number;
  form_id?: number;
  name: string;
  description?: string;
  order?: number; // Optional now, only used for migration
  total_weighting?: number;
  color?: string;
  icon?: string;
  created_at?: string;
}

// NEW: Section interface for navigation
export interface FormSection {
  id: number;
  form_id: number;
  name: string;
  description?: string;
  order: number;
  color?: string;
  icon?: string;
  question_count?: number;
  created_at?: string;
}

export interface FormQuestion {
  id: number;
  form_id: number;
  section_id: number; // NEW: Physical section
  category_id: number; // Conceptual category
  text: string;
  description?: string;
  type: 'yes_no' | 'scale' | 'text' | 'number' | 'select' | 'multiple' | 'date' | 'time';
  required: boolean;
  weighting: number;
  order: number;
  options?: any;
  config?: any;
  points_yes?: number;
  points_no?: number;
  points_na?: number;
  allows_evidence: boolean;
  allows_comment: boolean;
  section_name?: string; // For display
  category_name?: string; // For display
  created_at?: string;
}

export interface FormFilters {
  page?: number;
  limit?: number;
  search?: string;
  active?: boolean;
  is_template?: boolean;
}

// Servicio de Formularios
const formService = {
  // Listar formularios con filtros
  list: async (filters?: FormFilters) => {
    const response = await api.get('/forms', { params: filters });
    return response.data;
  },

  // Obtener formulario por ID
  getById: async (id: number) => {
    const response = await api.get(`/forms/${id}`);
    return response.data;
  },

  // Crear formulario
  create: async (data: { name: string; description?: string; is_template?: boolean }) => {
    const response = await api.post('/forms', data);
    return response.data;
  },

  // Actualizar formulario
  update: async (id: number, data: Partial<Form>) => {
    // Timeout extendido para formularios grandes
    const response = await api.put(`/forms/${id}`, data, { timeout: 120000 }); // 2 minutos
    return response.data;
  },

  // Actualización masiva (bulk update) - UNA sola petición
  bulkUpdate: async (id: number, data: {
    formInfo?: {
      name: string;
      description?: string;
      active?: boolean;
      is_template?: boolean;
    };
    sections?: {
      toCreate?: Array<{ tempId: number; name: string; description?: string; order: number; color?: string }>;
      toUpdate?: Array<{ id: number; name: string; description?: string; order: number; color?: string }>;
      toDelete?: number[];
    };
    categories?: {
      toCreate?: Array<{ tempId: number; name: string; description?: string; color?: string }>;
      toUpdate?: Array<{ id: number; name: string; description?: string; color?: string }>;
      toDelete?: number[];
    };
    questions?: {
      toCreate?: Array<any>;
      toUpdate?: Array<any>;
      toDelete?: number[];
    };
  }) => {
    const response = await api.put(`/forms/${id}/bulk-update`, data, { timeout: 180000 }); // 3 minutos
    return response.data;
  },

  // Eliminar formulario
  delete: async (id: number) => {
    const response = await api.delete(`/forms/${id}`);
    return response.data;
  },

  // Activar/desactivar formulario
  toggleStatus: async (id: number) => {
    const response = await api.patch(`/forms/${id}/toggle-status`);
    return response.data;
  },

  // Duplicar formulario
  duplicate: async (id: number, data: { name: string; version?: number }) => {
    const response = await api.post(`/forms/${id}/duplicate`, data);
    return response.data;
  },

  // ===== SECCIONES (NEW) =====

  // List sections of a form
  getSections: async (formId: number) => {
    const response = await api.get(`/forms/${formId}/sections`);
    return response.data;
  },

  // Create section
  createSection: async (formId: number, data: { name: string; description?: string; order?: number; color?: string; icon?: string }) => {
    const response = await api.post(`/forms/${formId}/sections`, data);
    return response.data;
  },

  // Update section
  updateSection: async (formId: number, sectionId: number, data: { name?: string; description?: string; order?: number; color?: string; icon?: string }) => {
    const response = await api.put(`/forms/${formId}/sections/${sectionId}`, data);
    return response.data;
  },

  // Delete section
  deleteSection: async (formId: number, sectionId: number) => {
    const response = await api.delete(`/forms/${formId}/sections/${sectionId}`);
    return response.data;
  },

  // Reorder sections
  reorderSections: async (formId: number, orderedIds: number[]) => {
    const response = await api.patch(`/forms/${formId}/sections/reorder`, { orderedIds });
    return response.data;
  },

  // Get questions by section
  getQuestionsBySection: async (formId: number, sectionId: number) => {
    const response = await api.get(`/forms/${formId}/sections/${sectionId}/questions`);
    return response.data;
  },

  // ===== CATEGORÍAS =====

  // Listar categorías de un formulario
  getCategories: async (formId: number) => {
    const response = await api.get(`/forms/${formId}/categories`);
    return response.data;
  },

  // Crear categoría
  createCategory: async (formId: number, data: { name: string; description?: string; color?: string; icon?: string }) => {
    const response = await api.post(`/forms/${formId}/categories`, data);
    return response.data;
  },

  // Actualizar categoría
  updateCategory: async (formId: number, categoryId: number, data: { name?: string; description?: string; color?: string; icon?: string }) => {
    const response = await api.put(`/forms/${formId}/categories/${categoryId}`, data);
    return response.data;
  },

  // Eliminar categoría
  deleteCategory: async (formId: number, categoryId: number) => {
    const response = await api.delete(`/forms/${formId}/categories/${categoryId}`);
    return response.data;
  },

  // ===== PREGUNTAS =====

  // Listar preguntas de un formulario
  getQuestions: async (formId: number, filters?: { section_id?: number; category_id?: number; type?: string }) => {
    const response = await api.get(`/forms/${formId}/questions`, { params: filters });
    return response.data;
  },

  // Crear pregunta (NEW: now requires section_id)
  createQuestion: async (formId: number, sectionId: number, data: Partial<FormQuestion>) => {
    const response = await api.post(`/forms/${formId}/sections/${sectionId}/questions`, data);
    return response.data;
  },

  // Actualizar pregunta
  updateQuestion: async (formId: number, questionId: number, data: Partial<FormQuestion>) => {
    const response = await api.put(`/forms/${formId}/questions/${questionId}`, data, { timeout: 60000 });
    return response.data;
  },

  // Eliminar pregunta
  deleteQuestion: async (formId: number, questionId: number) => {
    const response = await api.delete(`/forms/${formId}/questions/${questionId}`);
    return response.data;
  },

  // Actualizar orden de preguntas en lote
  updateQuestionsOrder: async (formId: number, questions: { id: number; order: number }[]) => {
    const response = await api.patch(`/forms/${formId}/questions/batch-order`, { updates: questions }, { timeout: 120000 });
    return response.data;
  },
};

export default formService;
